import { IActionContext } from '@msdyn365-commerce/core';
import { AttributeDataType, CustomerAttribute, ProductRefinerSource, ProductRefinerValue, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import getOrganizationCustomer, { GetOrganizationCustomerInput } from '../actions/get-organization-customer';
import { IAppSettingsInContext } from '../settings/app.settings.autogenerated';

function isGodmodeEnabled(context: IActionContext): boolean {
    const godmode = context.requestContext.url.requestUrl.searchParams.get('godmode')?.toLowerCase();

    return godmode === '1' || godmode === 'true';
}

function isValidRecordId(recordId: string | undefined): boolean {
    return !!recordId && !recordId.startsWith('!') && !Number.isInteger(recordId) && Number(recordId) > 0;
}

export function addOrUpdateZeroPriceRefiner(context: IActionContext, refiners?: ProductRefinerValue[]): ProductRefinerValue[] {
    // Check criteria for allowing user to see zero price products. Example:
    // - Has ?godmode=true in url
    const allowZeroPriceProducts = isGodmodeEnabled(context);

    if (!allowZeroPriceProducts) {
        const result = refiners ? [...refiners] : [];

        // Make sure we don't show zero price products
        const priceRefiner = result.find(x => x.RefinerRecordId === 0);

        if (priceRefiner) {
            // There is a price refiner, make sure it has a min value of 0.01 or more
            if (!priceRefiner.LeftValueBoundString || Number(priceRefiner.LeftValueBoundString) < 0.01) {
                priceRefiner.LeftValueBoundString = '0.01';
            }
        } else {
            // No price refiner, add one
            result.push({
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.ProductRefinerValue',
                Count: 0,
                DataTypeValue: 1,
                LeftValueBoundString: '0.01',
                ExtensionProperties: [],
                RefinerRecordId: 0,
                RefinerSourceValue: 3,
                RightValueBoundString: '1000000000',
                RowNumber: 0,
                UnitText: context.requestContext.channel?.Currency
            } as any);
        }

        return result;
    }

    return refiners || [];
}

export async function getAttributeRefiners(context: IActionContext, customerAttributes: CustomerAttribute[] | undefined, attributeRecordId: number, friendlyName?: string): Promise<ProductRefinerValue[]> {
    const result: ProductRefinerValue[] = [];
    const refiner: ProductRefinerValue = {
        DataTypeValue: 5,
        LeftValueBoundString: '0',
        RefinerRecordId: attributeRecordId,
        RefinerSourceValue: 1,
        RightValueBoundString: '0',
        UnitText: ''
    };

    result.push(refiner);

    try {
        let attributeValue = 0;

        if (context.requestContext.user.isAuthenticated) {
            const attribute = Number(customerAttributes?.find(x => x.RecordId === attributeRecordId)?.AttributeValue?.StringValue);

            if (isNaN(attribute)) {
                // Throw NaN error
                if (friendlyName) {
                    console.warn(`${friendlyName} attribute value is missing or not a number.`);
                } else {
                    console.warn(`Attribute with record id ${attributeRecordId} is missing a value or not a number.`);
                }
            } else {
                attributeValue = attribute;
            }
        } else {
            // If user is not logged in show all products
            attributeValue = 1;
        }

        if (attributeValue !== 0) {
            result.push({
                ...refiner,
                LeftValueBoundString: attributeValue.toString(),
                RightValueBoundString: attributeValue.toString()
            });
        }
    } catch (error) {
        if (friendlyName) {
            console.warn(`Failed to get ${friendlyName} customer attribute or parse value of attribute. No filter applied to search.`, error);
        } else {
            console.warn(`Failed to get customer attribute (record id: ${attributeRecordId}) or parse value of attribute. No filter applied to search.`, error);
        }
    }

    return result;
}

export async function getAttributeRefinersEqualOrLessThan(
    context: IActionContext,
    customerAttributes: CustomerAttribute[] | undefined,
    attributeRecordId: number,
    maxValue: number,
    friendlyName?: string
): Promise<ProductRefinerValue[]> {
    const result: ProductRefinerValue[] = [];
    const refiner: ProductRefinerValue = {
        DataTypeValue: 5,
        LeftValueBoundString: '0',
        RefinerRecordId: attributeRecordId,
        RefinerSourceValue: 1,
        RightValueBoundString: '0',
        UnitText: ''
    };

    result.push(refiner);

    try {
        let attributeValue = 0;

        if (context.requestContext.user.isAuthenticated) {
            const attribute = Number(customerAttributes?.find(x => x.RecordId === attributeRecordId)?.AttributeValue?.StringValue);

            if (isNaN(attribute)) {
                // Throw NaN error
                if (friendlyName) {
                    console.warn(`${friendlyName} attribute value is missing or not a number.`);
                } else {
                    console.warn(`Attribute with record id ${attributeRecordId} is missing a value or not a number.`);
                }
            } else {
                attributeValue = attribute;
            }
        } else {
            // If user is not logged in show all products
            attributeValue = maxValue;
        }

        for (let i = 0; i <= attributeValue; i++) {
            if (i === 0) {
                // Skip 0 since it's already added
                continue;
            }
            result.push({
                ...refiner,
                LeftValueBoundString: i.toString(),
                RightValueBoundString: i.toString()
            });
        }
    } catch (error) {
        if (friendlyName) {
            console.warn(`Failed to get ${friendlyName} customer attribute or parse value of attribute. No filter applied to search.`, error);
        } else {
            console.warn(`Failed to get customer attribute (record id: ${attributeRecordId}) or parse value of attribute. No filter applied to search.`, error);
        }
    }

    return result;
}

export async function addAttributeRefiner(
    context: IActionContext,
    customerAttributes: CustomerAttribute[] | undefined,
    searchCriteriaInput: ProductSearchCriteria,
    attributeRecordId: number,
    keepExisting = false
): Promise<void> {
    if (searchCriteriaInput.Refinement!.some(x => x.RefinerRecordId === attributeRecordId)) {
        if (keepExisting) {
            return;
        }

        // Remove existing, shouldn't be here
        searchCriteriaInput.Refinement = searchCriteriaInput.Refinement!.filter(x => x.RefinerRecordId !== attributeRecordId);
    }

    const refiners = await getAttributeRefiners(context, customerAttributes, attributeRecordId);

    searchCriteriaInput.Refinement = searchCriteriaInput.Refinement!.concat(refiners);
}

export async function addAttributeRefinerEqualOrLessThan(
    context: IActionContext,
    customerAttributes: CustomerAttribute[] | undefined,
    searchCriteriaInput: ProductSearchCriteria,
    attributeRecordId: number,
    maxValue: number,
    keepExisting = false
): Promise<void> {
    if (searchCriteriaInput.Refinement!.some(x => x.RefinerRecordId === attributeRecordId)) {
        if (keepExisting) {
            return;
        }

        // Remove existing, shouldn't be here
        searchCriteriaInput.Refinement = searchCriteriaInput.Refinement!.filter(x => x.RefinerRecordId !== attributeRecordId);
    }

    const refiners = await getAttributeRefinersEqualOrLessThan(context, customerAttributes, attributeRecordId, maxValue);

    searchCriteriaInput.Refinement = searchCriteriaInput.Refinement!.concat(refiners);
}

function getVisibilityGroupRefiners(context: IActionContext, customerAttributes: CustomerAttribute[] | undefined): ProductRefinerValue[] | undefined {
    const config = (context.requestContext.app as IAppSettingsInContext).config;
    const visibilityGroupAttributeRecordId: string | undefined = config?.visibilityGroupAttributeRecordId;

    if (!isValidRecordId(visibilityGroupAttributeRecordId)) {
        return;
    }

    const refiners: ProductRefinerValue[] = [];

    // Refiner for products without a visibility group
    refiners.push({
        DataTypeValue: AttributeDataType.Text,
        LeftValueBoundString: '0',
        RefinerRecordId: Number(visibilityGroupAttributeRecordId),
        RefinerSourceValue: ProductRefinerSource.Attribute,
        RightValueBoundString: '0',
        UnitText: ''
    });

    const visibilityGroupAttribute = customerAttributes?.find(x => x.RecordId === Number(visibilityGroupAttributeRecordId));
    const customerGroups = new Set(visibilityGroupAttribute?.AttributeValue?.StringValue
        ?.split('|')
        .map(group => group.trim())
        .filter(Boolean) || []);

    for (const customerGroup of customerGroups) {
        if (customerGroup === '0') {
            // Already added above
            continue;
        }

        refiners.push({
            DataTypeValue: AttributeDataType.Text,
            LeftValueBoundString: customerGroup,
            RefinerRecordId: Number(visibilityGroupAttributeRecordId),
            RefinerSourceValue: ProductRefinerSource.Attribute,
            RightValueBoundString: customerGroup,
            UnitText: ''
        });
    }

    return refiners;
}

function getHideFromAnonymousUsersRefiners(context: IActionContext): ProductRefinerValue[] | undefined {
    const config = (context.requestContext.app as IAppSettingsInContext).config;
    const hideFromAnonymousUsersAttributeRecordId: string | undefined = config?.hideFromAnonymousUsersProductAttributeRecordId;

    if (context.requestContext.user.isAuthenticated || !isValidRecordId(hideFromAnonymousUsersAttributeRecordId)) {
        return;
    }

    const refiners: ProductRefinerValue[] = [];

    // Refiner for products visible to anonymous users
    refiners.push({
        DataTypeValue: AttributeDataType.TrueFalse,
        LeftValueBoundString: '0',
        RefinerRecordId: Number(hideFromAnonymousUsersAttributeRecordId),
        RefinerSourceValue: ProductRefinerSource.Attribute,
        RightValueBoundString: '0',
        UnitText: ''
    });

    return refiners;
}

export async function addAllAttributeRefiners(context: IActionContext, searchCriteriaInput: ProductSearchCriteria): Promise<void> {
    const refiners = await getAllAttributeRefiners(context);

    if (refiners.length > 0) {
        searchCriteriaInput.Refinement = (searchCriteriaInput.Refinement ?? []).concat(refiners);
    }
}

export async function getAllAttributeRefiners(context: IActionContext) {
    const godmode = isGodmodeEnabled(context);

    if (godmode) {
        return [];
    }

    let customerAttributes: CustomerAttribute[] | undefined = [];

    if (context.requestContext.user.isAuthenticated) {
        const { apiSettings, user: { customerAccountNumber } } = context.requestContext;
        const customer = await getOrganizationCustomer(new GetOrganizationCustomerInput(apiSettings, customerAccountNumber!), context);
        customerAttributes = customer.Attributes;
    }

    const result: ProductRefinerValue[] = [];

    const visibilityGroupRefiners = getVisibilityGroupRefiners(context, customerAttributes);
    if (visibilityGroupRefiners) {
        result.push(...visibilityGroupRefiners);
    }

    const hideFromAnonymousUsersRefiners = getHideFromAnonymousUsersRefiners(context);
    if (hideFromAnonymousUsersRefiners) {
        result.push(...hideFromAnonymousUsersRefiners);
    }

    const attributeRecordIds: string[] = [
    ];

    for (const attributeRecordId of attributeRecordIds) {
        const id = Number(attributeRecordId);

        // Make sure it's a number and above 0
        if (!isNaN(id) && id > 0) {
            const refiners = await getAttributeRefiners(context, customerAttributes, id);
            result.push(...refiners);
        }
    }

    return result;
}